<template>
  <div>
    <editor
      v-bind="$attrs"
      v-on="$listeners"
      :mentions="macroIntellisense"
      @initialized="onInitiailized"
      @customize-modules="customizeQuillModules"
      :name="nameProp"
      ref="editor"
      :value="value"
      @blur="runSpellCheck"
    />
    <div class="d-flex justify-content-end status-overlay-row">
      <IconButton
        :class="'spell-check-status pb-0 ' + spellCheckStatusClass"
        icon="circle"
        @click="handleClickSpellCheckIcon"
        v-tooltip="iconHoverText"
      />
    </div>
    <div
      class="spell-check-box"
      ref="spellCheckBox"
      v-if="selectedWordToCheck"
      v-bind="spellCheckBoxPosition"
    >
      <SpellCheckBox
        :wordToCheck="wordToCheck"
        @close="closeSpellCheck"
        @ignore="ignoreWord"
        @ignoreAll="ignoreAll"
        @replace="handleReplaceWord"
        @addWord="handleAddWord"
        @replaceAll="handleReplaceAll"
        @move="setSpellCheckBoxPosition"
        :isForcedUpperCase="isForcedUpperCase"
      />
    </div>
    <Modal :status="isDictionaryMaintenanceOpen" @close="toggleDictionaryMaintenance">
      <EditUserDictionary :userId="currentUser.id" @close="toggleDictionaryMaintenance" />
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import MacroModule, { macroStore } from "@/modules/Quill/MacroIntellisense.js";
import Editor from "./Editor.vue";
import Macro from "@/modules/Quill/Macro";
import Quill from "devextreme-quill";
import { getDefaultStyles } from "@/modules/getDefaultStylesByField";
import SpellCheckBox from "../SpellCheckBox.vue";
import spellChecker from "@/mixins/spellChecker";
import { getTextFromHtml } from "@/modules/helpers";
import IconButton from "./IconButton.vue";
import EditUserDictionary from "../EditUserDictionary.vue";
import Modal from "./Modal.vue";

Quill.register({ "modules/macro": Macro });

export default {
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: String
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    autoFocus: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Editor,
    SpellCheckBox,
    IconButton,
    EditUserDictionary,
    Modal
  },
  mixins: [spellChecker],
  data: () => ({
    component: {},
    _editor: null,
    isSpellCheckRunning: false,
    problemWords: [],
    isDictionaryMaintenanceOpen: false
  }),
  mounted() {
    if (this.isExpanded) {
      this.expand();
    }
    if (this.autoFocus) {
      setTimeout(() => {
        this.focus();
      }, 400);
    }
  },
  methods: {
    onInitiailized({ component }) {
      if (component) {
        component.register({ "modules/mentions": MacroModule }, true);
      }
      this.component = component;
      this.$emit("editorReady", this.name);
    },
    focus() {
      if (this.$refs?.editor?.focus) {
        this.$refs.editor.focus();
      }
    },
    expand() {
      if (this.$refs?.editor?.expand) {
        this.$refs.editor.expand();
      }
    },
    collapse() {
      if (this.$refs?.editor) {
        this.$refs.editor.collapse();
      }
    },
    customizeQuillModules(config) {
      config.macro = {
        name: this.name,
        styles: this.styles,
        initialized: true,
        generalOnly: true
      };
    },
    async handleClickSpellCheckIcon() {
      if (!this.enableSpellchecker) {
        const confirm = await window.confirm(
          "Spell check is not enabled. Would you like to enable it?"
        );
        if (confirm) {
          this.$store.dispatch("applicationSettings/setUserSettings", { enableSpellchecker: true });
        }
      } else if (this.problemWords?.length) {
        this.runSpellCheckOnSave();
      } else {
        this.toggleDictionaryMaintenance();
      }
    },
    toggleDictionaryMaintenance() {
      this.isDictionaryMaintenanceOpen = !this.isDictionaryMaintenanceOpen;
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      applicationSettings: state => state.applicationSettings,
      currentLab: state => state.currentLab,
      MacroSearchWithPeriod: state => state.labSettings.MacroSearchWithPeriod,
      currentSpecimen: state => state.accessionStore.currentSpecimen,
      labSettings: state => state.labSettings,
      enableSpellchecker: state => state.applicationSettings.enableSpellchecker
    }),
    ...mapGetters(["permissions"]),
    webSpellCheckInstance() {
      if (this.$refs.editor) {
        return this.$refs.editor.webSpellCheckInstance;
      }
      return null;
    },
    nameProp() {
      return this.name || "GeneralEnabledMacro";
    },
    styles() {
      return getDefaultStyles(this.name, true);
    },
    macroIntellisense() {
      if (this.applicationSettings.macroAssist) {
        return [
          {
            displayExpr: "displayName",
            searchExpr: "displayName",
            valueExpr: "displayName",
            dataSource: macroStore(),
            marker: ".",
            minSearchLength: 2
          },
          {
            displayExpr: "displayName",
            searchExpr: "displayName",
            valueExpr: "displayName",
            dataSource: macroStore(),
            marker: "\\",
            minSearchLength: 2
          },
          {
            dataSource: ["C", "D", "M", "S"],
            marker: ";",
            minSearchLength: 0,
            isExtension: true
          }
        ];
      }
      return [];
    },
    spellCheckStatusClass() {
      if (!this.enableSpellchecker) {
        return "text-muted";
      }
      if (!getTextFromHtml(this.value)?.length) {
        return "text-primary";
      }
      if (this.isSpellCheckRunning) {
        return "text-warning";
      }
      if (this.problemWords?.length) {
        return "text-danger";
      }
      return "text-success";
    },
    iconHoverText() {
      if (!this.enableSpellchecker) {
        return "Spell checker is not enabled<br>Click to enable";
      }
      if (!getTextFromHtml(this.value)?.length) {
        return "No text";
      }
      if (this.isSpellCheckRunning) {
        return "Spell check running...";
      }
      if (this.problemWords?.length) {
        return `${this.problemWords.length} spelling error${
          this.problemWords.length > 1 ? "s" : ""
        } found`;
      }
      return "No spelling errors found";
    }
  }
};
</script>
<style lang="scss" scoped>
.toolbar {
  width: 100%;
}
.title {
  text-transform: capitalize;
  font-size: 1.2rem;
}
.is-invalid {
  border-color: #dc3545 !important;
}
.is-valid {
  border-color: #28a745 !important;
}

::v-deep .dx-icon,
::v-deep .dx-dropdowneditor-icon {
  &::before {
    color: #333 !important;
  }
}
.spell-check-box {
  position: absolute;
  top: 500px;
  left: 600px;
}
.spell-check-status {
  position: relative;
  display: flex;
  font-size: 0.5 em;
  transform: translateY(-2.25em);
  padding-right: 1.5em;
}
</style>
